import { Heading } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { ManagerAuthorization, ServiceType } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import type { ReportCategories } from '../../../../common/enums/reportCategories';
import type { ReportStatus } from '../../../../common/enums/reportStatus';
import type { AdminProcess } from './adminProcessDetailsTypes';
import { getJson } from '../../../store/fetch';
import { Button, ButtonContainer, FormDivider, LinkButton, Loading } from '../../elements/common';
import { PageTemplate } from '../../templates/PageTemplate';
import { CollapseWrapper, EditProcessContainer } from '../episodes/common';
import { PatientAvatarBlock } from '../patients/PatientAvatarBlock';
import { PatientActiveInsuranceContextProvider } from '../patients/PatientInsuranceHistoryContext';
import { PatientSideBar } from '../patients/patientSideBar/PatientSideBar';
import { AdminProcessCoupledProcesses } from './AdminProcessCoupledProcesses';
import { AdminProcessDetailsActors } from './AdminProcessDetailsActors';
import { AdminProcessDetailsCustomControls } from './AdminProcessDetailsCustomControls';
import { AdminProcessDetailsFinancialStatus } from './AdminProcessDetailsFinancialStatus';
import { AdminProcessDetailsHeader } from './AdminProcessDetailsHeader';
import { AdminProcessDetailsResendMessagesModal } from './AdminProcessDetailsResendMessagesModal';
import { AdminProcessDetailsTechnicalStatus } from './AdminProcessDetailsTechnicalStatus';
import { AdminProcessDetailsThirdPartyMessages } from './AdminProcessDetailsThirdPartyMessages';
import { AdminProcessDetailsTimestamps } from './AdminProcessDetailsTimestamps';
import { AdminProcessInvitationDetails } from './AdminProcessInvitationDetails';
import { AdminSendEdifactButton } from './AdminSendEdifactButton';

export interface IDiagnosticReport {
    category: ReportCategories;
    clarificationQAPerformer: string | null;
    clarificationQAPerformerSentDateTime: string | null;
    clarificationQARequester: string | null;
    clarificationQARequesterSentDateTime: string | null;
    created_at: string;
    dateSent: string | null;
    id: string;
    isUnpaid: boolean;
    performerOrganization: IOrganization;
    performerOrganizationId: string;
    performerPractitioner: IPractitioner;
    performerPractitionerId: string;
    reviewStatus: ReportStatus;
}

export interface IPractitioner {
    id?: string;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
    uid: string | null;
}

export interface IOrganization {
    name: string;
    txcsId: string | null;
}

export function AdminProcessDetails() {
    const { id } = useParams();
    const translate = getSelectedLanguage();
    const [adminProcess, setAdminProcess] = useState<AdminProcess | undefined>();
    const [showResendModal, setShowResendModal] = useState<boolean | undefined>();
    const { frontendSettings } = useFrontendSettings();

    assertHasAuthorization(ManagerAuthorization.adminProcessRead);

    useEffect(() => {
        if (!id) {
            throw Error('Missing requestId');
        }
        load(id);
    }, [id]);

    const buttons: JSX.Element[] = [];
    if (hasOneAuthorization(ManagerAuthorization.adminProcessEdit)) {
        if (adminProcess) {
            buttons.push(
                <AdminSendEdifactButton
                    adminProcess={adminProcess}
                    onReload={async () => window.location.reload()}
                />
            );
        }

        buttons.push(
            <Button
                color="primary"
                onClick={() => setShowResendModal(true)}
                outlined
            >
                {translate.processDetails.resendThirdPartyMessages}
            </Button>
        );
    }

    if (adminProcess && hasOneAuthorization(ManagerAuthorization.patientProcesses)) {
        let serviceTypeLabel;
        switch (adminProcess.service.serviceType) {
            case ServiceType.consultation:
                serviceTypeLabel = translate.request.consult;
                break;
            default:
                serviceTypeLabel = translate.serviceTypeEnum[adminProcess.service.serviceType];
                break;
        }
        buttons.push(
            <LinkButton
                color="primary"
                outlined
                to={`/episode?requestId=${id}`}
            >
                {`${translate.processDetails.view} ${serviceTypeLabel}`}
            </LinkButton>
        );
    }

    return (
        <Loading
            item={adminProcess}
            loaded={(loadedAdminProcess) => (
                <PatientActiveInsuranceContextProvider patientId={loadedAdminProcess.patient.id}>
                    <PageTemplate
                        sideBarNavigation={
                            <PatientSideBar
                                onReload={async () => await load(id)}
                                patient={loadedAdminProcess.patient}
                            />
                        }
                        sideBarProfile={
                            <PatientAvatarBlock
                                patient={loadedAdminProcess.patient}
                                size="large"
                            />
                        }
                        topBarNavigation={
                            <PatientSideBar
                                onReload={async () => await load(id)}
                                patient={loadedAdminProcess.patient}
                            />
                        }
                        topBarProfile={
                            <PatientAvatarBlock
                                patient={loadedAdminProcess.patient}
                                size="small"
                            />
                        }
                    >
                        <Heading
                            marginBottom={6}
                            size="xlarge"
                        >
                            {translate.request.processDetails}
                        </Heading>
                        <CollapseWrapper processFormId="processDetails">
                            <EditProcessContainer
                                experimentalCarepathOphthalmologyEnabled={
                                    frontendSettings.experimentalCarepathOphthalmologyEnabled
                                }
                            >
                                <AdminProcessDetailsHeader
                                    adminProcess={loadedAdminProcess}
                                    requestId={id}
                                    title={getServiceLabel(
                                        loadedAdminProcess.service.code,
                                        'request',
                                        loadedAdminProcess.service.name
                                    )}
                                />

                                <AdminProcessDetailsActors adminProcess={loadedAdminProcess} />

                                <FormDivider />

                                <AdminProcessInvitationDetails adminProcess={loadedAdminProcess} />

                                <AdminProcessDetailsTimestamps adminProcess={loadedAdminProcess} />

                                <AdminProcessDetailsCustomControls
                                    adminProcess={loadedAdminProcess}
                                    reload={async () => await load(id)}
                                />

                                <AdminProcessDetailsThirdPartyMessages
                                    adminProcess={loadedAdminProcess}
                                    reload={showResendModal === false}
                                />

                                <FormDivider />

                                <AdminProcessDetailsFinancialStatus adminProcess={loadedAdminProcess} />

                                <FormDivider />

                                <AdminProcessDetailsTechnicalStatus adminProcess={loadedAdminProcess} />

                                {showResendModal && (
                                    <AdminProcessDetailsResendMessagesModal
                                        adminProcess={loadedAdminProcess}
                                        onClose={() => setShowResendModal(false)}
                                    />
                                )}

                                <ButtonContainer
                                    alignRight
                                    buttons={buttons}
                                />
                            </EditProcessContainer>
                        </CollapseWrapper>
                        <AdminProcessCoupledProcesses
                            adminProcess={loadedAdminProcess}
                            requestId={id}
                        />
                    </PageTemplate>
                </PatientActiveInsuranceContextProvider>
            )}
        />
    );

    async function load(requestId?: string) {
        const result = await getJson(`/api/adminProcesses/${requestId}`);

        setAdminProcess(result.json);
    }
}
